// src/pages/reviewer/MySubmissions.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { IDSButton, IDSTabs, IDSTab, IDSTabPanel, IDSAlert, IDSSelect, IDSBadge } from "@inera/ids-react";
import { formatDateTimeNoSeconds } from "../../utils/dateTime";
import { SUBMISSION_STATUS_LABELS } from "../../utils/submissionStatus";
import {
  filterSubmissionsByStatus,
  partitionReviewerSubmissions,
} from "../../utils/e2eSubmissionPolicy";
import { getReviewerSubmissions } from "../../services/E2EAPI";
import SubmissionStatusBadge from "./SubmissionStatusBadge";
import ArchiveSearch from "./ArchiveSearch";
import {
  fetchE2EUserProfilesByIds,
  getE2EUserProfileDetails,
  resolveE2EUserDisplayName,
} from "../../utils/e2eUserProfiles";

export default function ReviewerSubmissions() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [userProfilesById, setUserProfilesById] = useState({});

  const collectUserIds = (rows) => {
    const ids = new Set();

    rows.forEach((submission) => {
      if (submission?.userId) {
        ids.add(String(submission.userId));
      }

      if (Array.isArray(submission?.reviewedBy)) {
        submission.reviewedBy
          .filter(Boolean)
          .forEach((reviewerId) => ids.add(String(reviewerId)));
      }
    });

    return Array.from(ids);
  };

  useEffect(() => {
    async function fetchSubmissions() {
      try {
        const data = await getReviewerSubmissions();
        setSubmissions(data);

        const userIds = collectUserIds(Array.isArray(data) ? data : []);
        const profiles = await fetchE2EUserProfilesByIds(userIds);
        setUserProfilesById(profiles);
      } catch (err) {
        console.error(err);
        setError(err.message || "Kunde inte hämta inlämningslistan. Försök ladda om sidan.");
      } finally {
        setLoading(false);
      }
    }

    if (isAuthenticated) {
      fetchSubmissions();
    }
  }, [isAuthenticated]);

  const {
    waitingSubmissions,
    assignedSubmissions,
    assignedToMeSubmissions,
  } = partitionReviewerSubmissions(submissions, user?.id);

  const filteredWaitingSubmissions = filterSubmissionsByStatus(waitingSubmissions, statusFilter);
  const filteredAssignedSubmissions = filterSubmissionsByStatus(assignedSubmissions, statusFilter);
  const filteredAssignedToMe = filterSubmissionsByStatus(assignedToMeSubmissions, statusFilter);

  const getUserDisplayName = (userId) => resolveE2EUserDisplayName(userId, userProfilesById);
  const getUserProfileDetails = (userId) => getE2EUserProfileDetails(userId, userProfilesById);

  const UserIdentityLabel = ({ userId, compact = false }) => {
    const displayName = getUserDisplayName(userId) || "—";
    const details = getUserProfileDetails(userId);

    if (!userId) {
      return <span>{displayName}</span>;
    }

    const email = details?.email || "";
    const organizationName = details?.organizationName || "";

    return (
      <span style={{ display: "inline-flex", flexDirection: "column", alignItems: compact ? "flex-start" : "center", lineHeight: 1.2 }}>
        <span>{displayName}</span>
        {!compact && (email ) && (
          <span style={{ fontSize: "0.75rem", color: "var(--ids-palette-neutral-60)" }}>
            {email}
          </span>
        )}
      </span>
    );
  };

  const getReviewers = (submission) => {
    if (!Array.isArray(submission?.reviewedBy)) return [];
    return [...new Set(submission.reviewedBy.filter(Boolean).map(String))];
  };

  const ReviewerIndicator = ({ submission }) => {
    const reviewers = getReviewers(submission);
    if (reviewers.length === 0) {
      return <span>Ej tilldelad</span>;
    }

    const previewReviewerIds = reviewers.slice(0, 2);
    const remaining = reviewers.length - previewReviewerIds.length;

    return (
      <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "0.4rem", flexWrap: "wrap" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap", justifyContent: "center" }}>
          {previewReviewerIds.map((reviewerId) => (
            <UserIdentityLabel
              key={`${submission.submissionId}-${reviewerId}`}
              userId={reviewerId}
              compact
            />
          ))}
          {remaining > 0 ? <span>{`+${remaining}`}</span> : null}
        </span>
        {reviewers.length > 1 ? <IDSBadge type="info">{reviewers.length} granskare</IDSBadge> : null}
      </div>
    );
  };

  const renderTable = (rows, isActive = false) => {
    if (rows.length === 0) return (
      <p className="ids-mt-4">
        Inga formulär att visa.
      </p>
    );

    return (
      <div style={{ width: "100%", overflowX: "auto" }}>
      <table className="ids-mb-4" style={{ width: "100%", minWidth: "1400px" }}>
        <thead>
          <tr>
            <th style={{ textAlign: "left", padding: "0.5rem 1rem" }}>Formulärnamn</th>
            <th style={{ textAlign: "center", padding: "0.5rem 1rem" }}>Inlämnad av</th>
            <th style={{ textAlign: "center", padding: "0.5rem 1rem" }}>Organisation</th>
            <th style={{ textAlign: "center", padding: "0.5rem 1rem" }}>Status</th>
            <th style={{ textAlign: "center", padding: "0.5rem 1rem" }}>Granskare</th>
            <th style={{ textAlign: "center", padding: "0.5rem 1rem" }}>Senast uppdaterad</th>
            <th style={{ textAlign: "center", padding: "0.5rem 1rem" }}>Åtgärd</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.submissionId} style={{ height: "60px" }}>
              <td style={{ textAlign: "left", maxWidth: "400px", wordBreak: "break-word", padding: "0.5rem 1rem" }}>
                {s.formTitle} v{s.version}
              </td>
              <td style={{ textAlign: "center", padding: "0.5rem 1rem" }}>
                <UserIdentityLabel userId={s.userId} />
              </td>
              <td style={{ textAlign: "center", padding: "0.5rem 1rem" }}>
                {getUserProfileDetails(s.userId)?.organizationName || "—"}
              </td>
              <td style={{ textAlign: "center", padding: "0.5rem 1rem" }}>
                <SubmissionStatusBadge status={s.status} />
              </td>
              <td style={{ textAlign: "center", padding: "0.5rem 1rem" }}>
                <ReviewerIndicator submission={s} />
              </td>
              <td style={{ textAlign: "center", padding: "0.5rem 1rem" }}>
                {s.updatedAt
                  ? formatDateTimeNoSeconds(s.updatedAt)
                  : "—"}
              </td>
              <td style={{ textAlign: "center", padding: "0.5rem 1rem" }}>
                <IDSButton size="s" style={{ minWidth: "200px" }} onClick={() =>
                  navigate(
                    isActive
                      ? `/e2e/reviewer/submissions/${s.submissionId}`
                      : `/e2e/reviewer/submissions/view/${s.submissionId}`
                  )
                }>
                  {isActive ? "Granska" : "Visa"}
                </IDSButton>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    );
  };

  if (loading) return <p>Laddar inskickade formulär…</p>;
  if (error) return (
    <div className="ids-m-6"><main className="ids-content ids-p-6" style={{ maxWidth: "1200px", margin: "2rem auto" }}>
      <IDSAlert type="error"><p>{error}</p></IDSAlert>
    </main></div>
  );

  return (
    <div className="ids-m-6">
      <main className="ids-content ids-p-6" style={{ maxWidth: "1600px", margin: "2rem auto" }}>
        {showSuccess && (
          <div style={{ marginBottom: "2rem" }}>
            <IDSAlert ribbon type="success">
              {alertMessage}
            </IDSAlert>
          </div>
        )}
        <h2 className="ids-heading-l">Inskickade formulär</h2>
        <p style={{ color: "var(--ids-palette-neutral-60)", marginTop: "0.25rem", marginBottom: "1.5rem" }}>
          Nedan visas formulär inskickade av användare. Välj en flik för att se formulär efter handläggningsstatus.
        </p>

        <div className="ids-mb-4" style={{ maxWidth: "320px" }}>
          <IDSSelect
            label="Filtrera på status"
            id="submissionStatusFilter"
            name="submissionStatusFilter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="ALL">Alla statusar</option>
            {Object.entries(SUBMISSION_STATUS_LABELS).map(([statusCode, statusLabel]) => (
              <option key={statusCode} value={statusCode}>
                {statusLabel}
              </option>
            ))}
          </IDSSelect>
        </div>

        <IDSTabs breakpoint="s" tabs={[
          <IDSTab label={`Att granska (${filteredWaitingSubmissions.length})`} />,
          <IDSTab label={`Mina granskningar (${filteredAssignedToMe.length})`} />,
          <IDSTab label={`Pågående (${filteredAssignedSubmissions.length})`} />,
          <IDSTab label="Slutförda" />
        ]}>
          <IDSTabPanel noFocus>{renderTable(filteredWaitingSubmissions, true)}</IDSTabPanel>
          <IDSTabPanel noFocus>{renderTable(filteredAssignedToMe, true)}</IDSTabPanel>
          <IDSTabPanel noFocus>{renderTable(filteredAssignedSubmissions, true)}</IDSTabPanel>
          <IDSTabPanel noFocus><ArchiveSearch /></IDSTabPanel>
        </IDSTabs>
      </main>
    </div>
  );
}